import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '../../../../../../components/atoms/button/button';
import { Modal } from '../../../../../../components/molecules/modal/modal';
import { useAuth } from '../../../../../../hooks/use-auth';
import { clientesApi } from '../../../../../../services/api/clientes.api';
import { messageFromError } from '../../../../../../store/slices/clientes.slice';
import {
  ClienteDetail,
  StatusKycCliente,
} from '../../../../../../types/cliente';
import { formatDocumento } from '../../../../components/format';

interface Props {
  detail: ClienteDetail;
  onApproved: () => void;
}

const Confirmation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;

  strong {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const fullName = (detail: ClienteDetail) =>
  `${detail.nome ?? ''} ${detail.sobrenome ?? ''}`.trim() || 'Cliente sem nome';

export const ManualKycApproval = ({ detail, onApproved }: Props) => {
  const { hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const canApprove =
    hasPermission('CLIENTE_KYC_APPROVE') &&
    detail.statusKyc !== StatusKycCliente.APROVADO;

  if (!canApprove) return null;

  const close = () => {
    if (!loading) setIsOpen(false);
  };

  const approve = async () => {
    setLoading(true);
    try {
      await clientesApi.approveKycManually(detail.id);
      toast.success('KYC aprovado manualmente.');
      setIsOpen(false);
      onApproved();
    } catch (error) {
      toast.error(
        messageFromError(error, 'Não foi possível aprovar o KYC do cliente.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        startIcon={<ShieldCheck size={14} />}
        onClick={() => setIsOpen(true)}
      >
        Aprovar KYC manualmente
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={close}
        title="Aprovar KYC manualmente"
        description="Esta ação aprovará o KYC independentemente do resultado atual da validação."
        size="sm"
        closeOnBackdrop={!loading}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={approve} loading={loading}>
              Confirmar aprovação
            </Button>
          </>
        }
      >
        <Confirmation>
          <span>
            Cliente: <strong>{fullName(detail)}</strong>
          </span>
          <span>
            Documento: <strong>{formatDocumento(detail.documento)}</strong>
          </span>
        </Confirmation>
      </Modal>
    </>
  );
};
